/**
 * The account card's overflow menu (frame 75:2, `IconButton` 207:7746) and its one action: stop
 * tracking the account in this campaign, `DELETE .../tracked-accounts/{id}`.
 */
import { useState, type ReactNode } from 'react';
import { Ellipsis } from 'lucide-react';

import { ConfirmDialog } from '../../../chrome/form';
import { IconHint } from '../../../chrome/hint';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { IconButton } from '../../../ui/icon-button';
import { useRemoveAccount } from './api/queries';
import { am } from './messages';

export interface RemoveAccountProps {
  campaignId: string;
  trackedAccountId: string;
  name: string;
  onRemoved?: () => void;
  // Given, the caller opens the confirm from its own menu and no trigger renders here.
  confirming?: boolean;
  onConfirmingChange?: (open: boolean) => void;
}

export function RemoveAccount(props: RemoveAccountProps): ReactNode {
  const remove = useRemoveAccount(props.campaignId, props.trackedAccountId);
  const [ownConfirming, setOwnConfirming] = useState(false);
  const controlled = props.confirming !== undefined;
  const confirming = props.confirming ?? ownConfirming;
  const setConfirming = props.onConfirmingChange ?? setOwnConfirming;

  function handleOpenChange(open: boolean): void {
    setConfirming(open);
    if (!open) remove.reset();
  }

  return (
    <>
      {!controlled && (
        <DropdownMenu>
          <IconHint label={am('remove.menu')}>
            <DropdownMenuTrigger asChild>
              <IconButton label={am('remove.menu')} className="self-end sm:self-auto">
                <Ellipsis aria-hidden />
              </IconButton>
            </DropdownMenuTrigger>
          </IconHint>
          <DropdownMenuContent align="end">
            <DropdownMenuItem variant="destructive" onSelect={() => setConfirming(true)}>
              {am('remove.action')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={handleOpenChange}
        title={am('remove.title', { name: props.name })}
        description={am('remove.description')}
        confirmLabel={am('remove.confirm')}
        destructive
        pending={remove.isPending}
        failure={remove.error?.failure ?? null}
        onConfirm={() => remove.mutateAsync().then(() => props.onRemoved?.())}
      />
    </>
  );
}
